import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { brandSettingsCandidatesPath } from "@/routes"
import type { BrandSettingsCandidatesProps } from "@/types/candidates"

/**
 * OKS-950 — Brand Settings → Candidat·e·s.
 *
 * Owns `candidate_validation` (CTPO D4): whether an agency's approval needs
 * the brand's sign-off before a candidate enters the crew.
 *
 * **RadioGroup, not Select** (UX Spec §2). Each option carries a one-line
 * consequence, and a Select can only show the chosen label — the whole point
 * of this control is that "Never" means something a user needs told. The
 * explanations are wired via `aria-describedby` so a screen reader hears the
 * consequence, not just the word.
 *
 * **No shadcn `Form`, no react-hook-form, no zod** — Inertia `<Form>` with a
 * `name`-attributed field and render-prop errors, per the Crew rules.
 *
 * **Read-only tier** follows the shipped `can_edit` pattern
 * (`BrandSettingsInvites`): inputs disabled, Save hidden, one line saying why.
 * ⚠️ Note the policy behind `can_edit` deliberately DENIES agency members the
 * write, unlike every sibling settings policy — an agency must not be able to
 * switch off the brand's oversight of the agency's own picks. See
 * `BrandSettingsCandidatesPolicy`.
 */
export default function BrandSettingsCandidates({
  brand,
  options,
  has_agency,
  can_edit,
  pitch,
  derived_max_attempts,
}: BrandSettingsCandidatesProps) {
  const { t } = useTranslation()

  /**
   * OQ11's neutral line. Renders on exactly one combination — the brand has an
   * agency AND the routing is `never` — and on no other. It is muted text, not
   * an `Alert`: `never` with an agency is a legitimate deliberate choice, and a
   * destructive banner would read as an error the user has to fix.
   *
   * Both halves are server-supplied. `has_agency` is the ONLY read of
   * `current_agency` in the feature and feeds copy alone — the setting itself
   * must never be derived from it (PRD AC2).
   */
  const showAgencyNeverNotice =
    has_agency && brand.candidate_validation === "never"

  return (
    <AppLayout>
      <Head title={t("candidates.validation_settings.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-candidates-settings" className="space-y-6">
          <HeadingSmall
            title={t("candidates.validation_settings.title")}
            description={t("candidates.validation_settings.subtitle")}
          />

          <Form
            method="patch"
            action={brandSettingsCandidatesPath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="brand-candidates-settings-form"
          >
            {({ errors, processing }) => (
              <>
                <fieldset className="grid gap-3">
                  <legend className="mb-2 text-sm font-medium">
                    {t("candidates.validation_settings.legend")}
                  </legend>

                  <RadioGroup
                    name="brand[candidate_validation]"
                    defaultValue={brand.candidate_validation}
                    disabled={!can_edit}
                    aria-invalid={
                      errors.candidate_validation ? true : undefined
                    }
                  >
                    {options.map((value) => (
                      <div key={value} className="flex items-start gap-3">
                        <RadioGroupItem
                          value={value}
                          id={`candidate_validation_${value}`}
                          aria-describedby={`candidate_validation_${value}_description`}
                          data-testid={`brand-candidates-settings-option-${value}`}
                          className="mt-1"
                        />
                        <div className="grid gap-1">
                          <Label htmlFor={`candidate_validation_${value}`}>
                            {t(
                              `candidates.validation_settings.options.${value}.label`,
                            )}
                          </Label>
                          {/*
                            The consequence, not a restatement of the label.
                            Linked by `aria-describedby` above so it is read
                            out with the option rather than skipped.
                          */}
                          <p
                            id={`candidate_validation_${value}_description`}
                            className="text-muted-foreground text-sm"
                          >
                            {t(
                              `candidates.validation_settings.options.${value}.description`,
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>

                  <InputError
                    messages={errors.candidate_validation}
                    data-testid="brand-candidates-settings-error"
                  />
                </fieldset>

                {showAgencyNeverNotice && (
                  <p
                    data-testid="brand-candidates-settings-agency-never-notice"
                    role="status"
                    className="text-muted-foreground text-sm"
                  >
                    {t("candidates.validation_settings.agency_never_notice")}
                  </p>
                )}

                {/*
                  OKS-951 — outreach cadence + pitch template, in the SAME form
                  and under the SAME Save. One button for one page: splitting
                  them would make "did my change stick?" depend on which field
                  the user last touched.

                  Every numeric field carries its consequence in plain language
                  (UX Spec §5) — a number with no consequence line is a setting
                  nobody dares change.
                */}
                <fieldset className="grid gap-4 border-t pt-6">
                  <legend className="text-sm font-medium">
                    {t("candidates.outreach_settings.legend")}
                  </legend>

                  <div className="grid gap-1.5">
                    <Label htmlFor="pitch_body">
                      {t("candidates.outreach_settings.pitch.label")}
                    </Label>
                    <Textarea
                      id="pitch_body"
                      name="pitch[body]"
                      rows={8}
                      defaultValue={pitch.body}
                      disabled={!can_edit}
                      aria-invalid={errors.pitch_body ? true : undefined}
                      aria-describedby="pitch_body_fields"
                      data-testid="brand-candidates-settings-pitch-body"
                    />
                    {/*
                      The legal fields are LISTED rather than discovered by
                      being refused. Server-supplied from the renderer's own
                      constant, so this can never offer a field the validator
                      would reject.
                    */}
                    <p
                      id="pitch_body_fields"
                      className="text-muted-foreground text-sm"
                    >
                      {t("candidates.outreach_settings.pitch.merge_fields", {
                        fields: pitch.merge_fields
                          .map((f) => `{{${f}}}`)
                          .join(" · "),
                      })}
                    </p>
                    <InputError
                      messages={errors.pitch_body}
                      data-testid="brand-candidates-settings-pitch-error"
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="pitch_crew_name">
                      {t("candidates.outreach_settings.crew_name.label")}
                    </Label>
                    <Input
                      id="pitch_crew_name"
                      name="pitch[crew_name]"
                      defaultValue={pitch.crew_name}
                      disabled={!can_edit}
                      data-testid="brand-candidates-settings-crew-name"
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="pitch_perks">
                      {t("candidates.outreach_settings.perks.label")}
                    </Label>
                    <Input
                      id="pitch_perks"
                      name="pitch[perks]"
                      defaultValue={pitch.perks}
                      disabled={!can_edit}
                      data-testid="brand-candidates-settings-perks"
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="follow_up_offset_days">
                      {t("candidates.outreach_settings.offsets.label")}
                    </Label>
                    {/*
                      A comma-separated text input rather than N numeric inputs:
                      the list is variable-length (1..5), and a fixed set of
                      inputs cannot express "two follow-ups" vs "three" without
                      an add/remove-row UI this setting does not warrant.
                    */}
                    <Input
                      id="follow_up_offset_days"
                      name="brand[follow_up_offset_days]"
                      defaultValue={brand.follow_up_offset_days.join(", ")}
                      disabled={!can_edit}
                      aria-invalid={
                        errors.follow_up_offset_days ? true : undefined
                      }
                      aria-describedby="follow_up_offset_days_help"
                      data-testid="brand-candidates-settings-offsets"
                    />
                    <p
                      id="follow_up_offset_days_help"
                      className="text-muted-foreground text-sm"
                    >
                      {t("candidates.outreach_settings.offsets.consequence", {
                        days: brand.follow_up_offset_days.join(", "),
                      })}
                    </p>
                    <InputError messages={errors.follow_up_offset_days} />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="auto_close_after_days">
                      {t("candidates.outreach_settings.auto_close.label")}
                    </Label>
                    <Input
                      id="auto_close_after_days"
                      name="brand[auto_close_after_days]"
                      inputMode="numeric"
                      defaultValue={String(brand.auto_close_after_days)}
                      disabled={!can_edit}
                      aria-invalid={
                        errors.auto_close_after_days ? true : undefined
                      }
                      aria-describedby="auto_close_after_days_help"
                      data-testid="brand-candidates-settings-auto-close"
                    />
                    <p
                      id="auto_close_after_days_help"
                      className="text-muted-foreground text-sm"
                    >
                      {t(
                        "candidates.outreach_settings.auto_close.consequence",
                        { count: brand.auto_close_after_days },
                      )}
                    </p>
                    <InputError messages={errors.auto_close_after_days} />
                  </div>

                  {/*
                    🔴 READ-ONLY, never an input. Max attempts is DERIVED from
                    the offsets list; a stored maximum that can disagree with
                    that length is a drift bug with no symptom (PRD R3-a), so
                    there must be no way to set one.
                  */}
                  <p
                    data-testid="brand-candidates-settings-derived-max"
                    className="text-muted-foreground text-sm"
                  >
                    {t("candidates.outreach_settings.derived_max", {
                      count: derived_max_attempts,
                    })}
                  </p>
                </fieldset>

                {!can_edit && (
                  <p
                    data-testid="brand-candidates-settings-readonly-hint"
                    role="status"
                    className="text-muted-foreground text-sm"
                  >
                    {t("candidates.validation_settings.read_only_hint")}
                  </p>
                )}

                {can_edit && (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="brand-candidates-settings-save"
                  >
                    {processing
                      ? t("candidates.validation_settings.saving")
                      : t("candidates.validation_settings.save")}
                  </Button>
                )}
              </>
            )}
          </Form>
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
